import { Subject } from 'rxjs';
import { Gizmo } from '../../../gizmos';
import {
  IBinderSetDataParams,
  IGizmoData,
  IGizmoEvent,
  IIndex,
  IIndexColumn,
  IIndexOperation,
  IRealmFilledForm,
  TGizmoData,
} from '../../../types';
import setData from './set-data';
import { gnov } from '../../../utils';
import CommonBinder from '../../common-binder';

interface IAnnexFormBinder {
  init(): Promise<void>;
}

interface IAnnexFormNotice {
  type:
    | 'child.form.lyfecycle.after-submit'
    | 'child.form.lyfecycle.delete'
    | 'child.form.lyfecycle.cancel';
}

class AnnexFormBinder extends CommonBinder implements IAnnexFormBinder {
  private _index?: IIndex;
  public index: Subject<IIndex>;
  /**
   * This subject is used by the core to send notices to the platform layer, right now it's being
   * used only to notify the platform layer that a child root gizmo has been saved
   */
  public notices: Subject<IAnnexFormNotice>;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this.index = new Subject<IIndex>();
    this.notices = new Subject<IAnnexFormNotice>();
    this._gizmo.data.subscribe(async (data: TGizmoData) => {
      if ((data && Array.isArray(data)) || data === undefined) {
        await this._setIndex(data);
      }
    });
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const initData = this._gizmo.getData();

      if (initData && Array.isArray(initData)) {
        await this._setIndex(initData);
      }

      resolve();
    });
  };

  /**
   * Get the current index data
   *
   * @returns {IIndex} index data
   */
  public getIndex = (): IIndex | undefined => {
    return this._index;
  };

  /**
   * Get the cell value that will be rendered on the platform layer
   *
   * @param {IIndexColumn} column column's configuration
   * @param {any} row data row
   *
   * @returns object with the info that's going to be rendered
   */
  public getCell = ({ column, row }: IGetCellParams) => {
    if (
      column.type === 'operations' &&
      column.operations &&
      Array.isArray(column.operations)
    ) {
      return column.operations.map((operation: IIndexOperation) => {
        return { v: operation.label, op: operation.type };
      });
    } else {
      const columns = gnov(row, 'afd.columns');

      if (columns) {
        const col = columns.find((c: any) => c.f_id === column.fid);

        if (col && col.value) {
          return { v: col.value };
        }
      }
    }

    return undefined;
  };

  /**
   * Delete an annex form from the index
   * ! DELETE IT ALSO FROM THE CACHE, RIGHT NOW WE'RE LEAVING IT LIKE THIS BECAUSE
   * ! FOR THE CURRENT PUBLIC FORM CASE IT'S NOT NECESSARY
   * @param {any} row row object taht comes from clicking the button on the platform layer
   */
  public deleteAnnexForm = ({ row }: IDeleteAnnexFormParams): Promise<void> => {
    return new Promise<void>(async resolve => {
      const gizmoData = (this._gizmo.getData() as IGizmoData[]) || [];

      const newGizmoData = gizmoData.filter((gD: IGizmoData) => {
        return gD.afd?.filledFormId !== row.afd?.filledFormId;
      });

      if (newGizmoData.length > 0) {
        await this._gizmo.setData(newGizmoData);
      } else {
        await this._gizmo.setData(undefined);
      }

      resolve();
    });
  };

  /**
   * Get the params that will be used to create a new child 'annex_form'
   *
   * @returns
   */
  public getNewAnnexFormParams = (): any => {
    const data = this._gizmo.getData();
    const callbacks = this._gizmo.getCallbacks();
    const config = this._gizmo.getConfig();
    const parentRootGizmoFid = callbacks.getInfoCB({
      type: 'rootGizmoFid',
    });
    const parentRootGizmoData = callbacks.getInfoCB({
      type: 'rootGizmoData',
    });

    let childIndex = 0;
    // TODO: handle how to get the total children
    // let totalChildren = 0;
    if (data && Array.isArray(data)) {
      // If data exists it means that we already have children
      childIndex = data.length;
      // totalChildren = data.length + 1;
    }

    return {
      rootGizmoFid: config.ops?.annexForm?.rootGizmoFid,
      parentRootGizmoFid,
      parentGizmoFid: config.fid,
      parentDataId: parentRootGizmoData.id,
      parentDataFid: parentRootGizmoData.fid,
      childIndex,
      // totalChildren,
    };
  };

  /**
   * Get the params that will be used to display the child annex form
   *
   * @param {any} row row object that comes from clicking the button on the
   */
  public getAnnexFormParams = ({ row }: IGetAnnexFormParams): any => {
    const callbacks = this._gizmo.getCallbacks();
    const parentRootGizmoFid = callbacks.getInfoCB({
      type: 'rootGizmoFid',
    });
    const parentRootGizmoData = callbacks.getInfoCB({
      type: 'rootGizmoData',
    });
    const childIndex = this._index?.rows.findIndex(
      r => row.afd?.filledFormId === gnov(r, 'afd.filledFormId')
    );

    const returnParams: any = {
      dataId: row.afd?.id,
      dataFid: row.afd?.filledFormId,
      rootGizmoFid: row.afd?.formId,
      parentRootGizmoFid,
      parentGizmoFid: this._gizmo.getConfig().fid,
      parentDataId: parentRootGizmoData.id,
      parentDataFid: parentRootGizmoData.fid,
      childIndex,
    };
    const gizmoParams = this._gizmo.getParams();
    if (gizmoParams['m'] === 's') {
      returnParams['m'] = 's';
    }

    return returnParams;
  };

  /**
   * This method is called when a child gizmo is submitted
   *
   * @param {IGizmoEvent} event gizmo event destined to this gizmo
   * @returns
   */
  public handleChildSubmitEvent = ({
    event,
  }: IHandleChildSubmitEventParams): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      const childRootGizmoData = event.data?.rootGizmoOperation?.rootGizmoData;
      const rootGizmoData = this._gizmo.getCallbacks().getInfoCB({
        type: 'rootGizmoData',
      });

      if (
        childRootGizmoData?.belongsTo &&
        typeof childRootGizmoData.belongsTo !== 'boolean' &&
        childRootGizmoData.belongsTo.filledFormId === rootGizmoData.fid &&
        childRootGizmoData.belongsTo.answerId === config.fid &&
        config.ops?.annexForm?.columns
      ) {
        // If the belongsTo data corresponds to this rootGizmoData and gizmo,
        // we're going to loop through the configured columns and geneate the 'newColumns'
        // with the child RootGizmoData, this column is going to be assigned to the
        // GizmoData which will be updated
        const newColumns = config.ops?.annexForm.columns.map(column => {
          if (childRootGizmoData.d) {
            const gData = childRootGizmoData.d[column.f_id];

            if (gData && !Array.isArray(gData)) {
              return {
                f_id: column.f_id,
                number: column.number,
                title: gData.v.toString(),
                value: gData.v.toString(),
              };
            }
          }

          return {
            ...column,
            value: '',
          };
        });

        const gizmoData = (this._gizmo.getData() as IGizmoData[]) || [];
        let gDataExists = false;

        for (const gD of gizmoData) {
          if (gD.afd?.filledFormId === childRootGizmoData.fid) {
            gD.afd.columns = newColumns;

            gDataExists = true;
          }
        }

        if (!gDataExists) {
          const gData: IGizmoData = {
            v: '',
            afd: {
              columns: newColumns,
              filledFormId: childRootGizmoData.fid,
              formId: childRootGizmoData.formId!,
              id: childRootGizmoData.id,
              editable: true,
            },
          };

          gizmoData.push(gData);
        }

        await this._gizmo.setData(gizmoData);
        // TODO: send notification to the UI
        this.notices.next({
          type: 'child.form.lyfecycle.after-submit',
        });
      }

      resolve();
    });
  };

  /**
   * Method called when a child gizmo is cancelled
   *
   * @returns
   */
  public handleChildCancelEvent = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      this.notices.next({
        type: 'child.form.lyfecycle.cancel',
      });

      resolve();
    });
  };

  public setData = async ({
    data,
    event,
  }: IBinderSetDataParams): Promise<void> => {
    if (data && Array.isArray(data)) {
      const d = await setData({ data, event, context: { gizmo: this._gizmo } });
      if (d) {
        await this._gizmo.setData(d);
        await this._setIndex(d);
      }
    }
  };

  private _setIndex = async (data?: IGizmoData[]) => {
    const config = this._gizmo.getConfig();
    const columns: IIndexColumn[] = [];

    if (config.ops?.annexForm?.columns) {
      const { columns: gizmoColumns } = config.ops.annexForm;

      for (const gColumn of gizmoColumns) {
        columns.push({
          fid: gColumn.f_id,
          label: gColumn.title,
        });
      }

      const gizmoParams = this._gizmo.getParams();
      const operationsColumn: IIndexColumn = {
        fid: 'c111',
        type: 'operations',
        label: 'Operations',
        operations: [
          {
            label: 'Editar',
            type: 'register.edit',
          },
          {
            label: 'Eliminar',
            type: 'register.delete',
          },
        ],
      };
      if (gizmoParams['m'] === 's') {
        operationsColumn.operations = [
          {
            label: 'Ver',
            type: 'register.show',
          },
        ];
      }

      columns.push(operationsColumn);

      let rows: any[] = data || [];
      if (data) {
        const database = this._gizmo.getDao()?.getDatabase();

        if (database) {
          rows = [];

          for (const d of data) {
            if (d.afd?.filledFormId && d.afd.formId) {
              try {
                const filledForm: IRealmFilledForm = await database.get({
                  type: 'filledForms',
                  fid: d.afd.filledFormId,
                });

                if (filledForm.gizmoData.answers) {
                  const cols: any = [];
                  d.fid = filledForm.gizmoData.fid;

                  for (const column of columns) {
                    if (column.fid) {
                      const value = gnov(
                        filledForm.gizmoData.answers,
                        `${column.fid}.0.value`
                      );

                      if (value !== undefined) {
                        cols.push({
                          f_id: column.fid,
                          value: value.toString(),
                        });
                      }
                    }
                  }

                  // We're going to clone it because we don't want to modify the original
                  // const dd = clone(d);
                  const dd = d;
                  if (dd.afd) {
                    dd.afd.columns = cols;
                  }

                  rows.push(dd);
                }
              } catch (e) {}
            }
          }
        }
      }

      const index: IIndex = {
        fid: 'index',
        pagination: {
          page: 1,
          pageSize: 10,
          total: data?.length || 0,
        },
        columns,
        rows,
      };

      this._index = index;
      this.index.next(index);
    }
  };
}

interface IGetAnnexFormParams {
  // operation: TPlatformOperation;
  row: IGizmoData;
}

interface IDeleteAnnexFormParams {
  row: IGizmoData;
}

interface IGetCellParams {
  column: IIndexColumn;
  row: any;
}

interface IHandleChildSubmitEventParams {
  event: IGizmoEvent;
}

export default AnnexFormBinder;
